import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Stack,
  Title,
  Paper,
  Button,
  Group,
  Alert,
  Text,
  Card,
  Badge,
  Table,
  ActionIcon,
  Tooltip,
  Select,
  Skeleton,
  Switch,
  NumberInput,
  TextInput,
} from '@mantine/core';
import {
  IconTrash,
  IconRefresh,
  IconDatabase,
  IconRocket,
  IconCheck,
  IconX,
  IconClock,
  IconInfoCircle,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useAppUser } from '../../hooks/useAppUser';
import { usePageTitle } from '../../hooks/usePageTitle';
import { collection, query, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useFirestoreTrigger } from '../../hooks/useFirestoreTrigger';

type CacheInfo = {
  name: string;
  displayName: string;
  createdAt: string;
  expiresAt: string;
  isExpired: boolean;
  totalTokenCount?: number;
};

type Migration = {
  id: string;
  type: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  dryRun: boolean;
  totalUsers: number;
  processedUsers: number;
  failedUsers: number;
  startedAt: string;
  completedAt?: string;
  createdBy: string;
  errorMessage?: string;
};

export const AdminSystemPage = () => {
  usePageTitle('Admin System | Meals You Love');
  const [globalFlags, setGlobalFlags] = useState<Record<string, boolean | undefined>>({});
  const [globalFlagUpdating, setGlobalFlagUpdating] = useState<string | null>(null);
  // Which meals the weekly scheduled seeding run targets and how big each
  // run is; null until loaded
  const [seedingConfig, setSeedingConfig] = useState<Record<string, { enabled: boolean; promptCount?: number }> | null>(null);
  const [seedingDefaults, setSeedingDefaults] = useState({
    promptCount: 6,
    recipesPerPrompt: 5,
    promptCountMin: 1,
    promptCountMax: 10,
  });
  const [seedingConfigUpdating, setSeedingConfigUpdating] = useState<string | null>(null);
  const { user } = useAppUser();
  const [cachesLoading, setCachesLoading] = useState(false);
  const [caches, setCaches] = useState<CacheInfo[]>([]);
  const [migrationsLoading, setMigrationsLoading] = useState(false);
  const [migrations, setMigrations] = useState<Migration[]>([]);
  const [startingMigration, setStartingMigration] = useState(false);
  const [selectedMigrationType, setSelectedMigrationType] = useState<string | null>('nutrition-backfill');

  const loadCaches = useCallback(async () => {
    if (!user) return;

    setCachesLoading(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/caches`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCaches(data.caches);
      } else {
        notifications.show({
          title: 'Error',
          message: 'Failed to load caches',
          color: 'red',
        });
      }
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to load caches',
        color: 'red',
      });
    } finally {
      setCachesLoading(false);
    }
  }, [user]);

  const handleDeleteCache = async (cacheName: string) => {
    if (!user) return;

    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/caches/${encodeURIComponent(cacheName)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        notifications.show({
          title: 'Success',
          message: 'Cache deleted successfully',
          color: 'green',
        });
        // Reload caches
        loadCaches();
      } else {
        const error = await response.json();
        notifications.show({
          title: 'Error',
          message: error.error || 'Failed to delete cache',
          color: 'red',
        });
      }
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to delete cache',
        color: 'red',
      });
    }
  };

  const loadMigrations = useCallback(async (showLoading = true) => {
    if (!user) return;

    if (showLoading) {
      setMigrationsLoading(true);
    }
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/migrations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setMigrations(data.migrations);
      } else {
        notifications.show({
          title: 'Error',
          message: 'Failed to load migrations',
          color: 'red',
        });
      }
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to load migrations',
        color: 'red',
      });
    } finally {
      if (showLoading) {
        setMigrationsLoading(false);
      }
    }
  }, [user]);

  // Live-refresh migrations while any is in flight: watch the in-flight
  // migration docs (readable via the admin claim) and reload the list on
  // every progress write, so status and counts update without a manual
  // refresh. Falls back to 5s polling if the listener fails.
  const hasActiveMigrations = migrations.some(
    migration => migration.status === 'pending' || migration.status === 'in_progress'
  );
  const inFlightMigrationsQuery = useMemo(
    () => query(collection(db, 'migrations'), where('status', 'in', ['pending', 'in_progress'])),
    []
  );
  useFirestoreTrigger({
    enabled: hasActiveMigrations,
    target: inFlightMigrationsQuery,
    fallbackIntervalMs: 5000,
    onTrigger: () => loadMigrations(false), // background refresh, no spinner
  });

  const handleStartMigration = async (type: string, dryRun: boolean) => {
    if (!user) return;

    setStartingMigration(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/migrations/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ type, dryRun }),
      });

      if (response.ok) {
        const data = await response.json();
        notifications.show({
          title: 'Migration started',
          message: `${dryRun ? 'Dry run' : 'Migration'} started for ${data.totalUsers} users`,
          color: 'green',
        });
        // Reload migrations list
        loadMigrations();
      } else {
        const error = await response.json();
        notifications.show({
          title: 'Error',
          message: error.error || 'Failed to start migration',
          color: 'red',
        });
      }
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to start migration',
        color: 'red',
      });
    } finally {
      setStartingMigration(false);
    }
  };

  useEffect(() => {
    if (!user?.isAdmin) return;
    loadCaches();
    loadMigrations();
  }, [user, loadCaches, loadMigrations]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const token = await user.getIdToken();
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/feature-flags/global`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setGlobalFlags(data.featureFlags || {});
        }
      } catch (error) {
        console.error('Failed to load global feature flags:', error);
      }
      try {
        const token = await user.getIdToken();
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/catalog-seeding-config`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setSeedingConfig(data.config || {});
          if (data.defaults) setSeedingDefaults(data.defaults);
        }
      } catch (error) {
        console.error('Failed to load catalog seeding config:', error);
      }
      try {
        const token = await user.getIdToken();
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/marketing/launch-campaigns`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setLaunchCampaigns(data.campaigns || []);
          if (data.campaigns?.length === 1) setLaunchCampaignId(data.campaigns[0].id);
        }
      } catch (error) {
        console.error('Failed to load launch campaigns:', error);
      }
    })();
  }, [user]);

  const handleToggleGlobalFlag = async (flag: string, enabled: boolean) => {
    if (!user) return;
    setGlobalFlagUpdating(flag);
    const previous = globalFlags;
    setGlobalFlags(prev => ({ ...prev, [flag]: enabled }));
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/feature-flags/global`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ featureFlags: { [flag]: enabled } }),
      });
      if (!response.ok) throw new Error('Request failed');
      notifications.show({
        message: `${flag} is now ${enabled ? 'ON' : 'OFF'} globally`,
        color: enabled ? 'green' : 'orange',
        autoClose: 3000,
      });
    } catch (error) {
      setGlobalFlags(previous);
      notifications.show({
        title: 'Error',
        message: `Failed to update global flag ${flag}`,
        color: 'red',
      });
    } finally {
      setGlobalFlagUpdating(null);
    }
  };

  // Launch-email campaign state: registry list, selection, test address,
  // and the last dry-run/send result for display
  const [launchCampaigns, setLaunchCampaigns] = useState<
    { id: string; label: string; requiredFlag: string | null }[]
  >([]);
  const [launchCampaignId, setLaunchCampaignId] = useState<string | null>(null);
  const [launchTestEmail, setLaunchTestEmail] = useState('');
  const [launchEmailResult, setLaunchEmailResult] = useState<{
    dryRun: boolean;
    flagGloballyEnabled: boolean;
    counts: Record<string, number>;
  } | null>(null);
  const [launchEmailRunning, setLaunchEmailRunning] = useState(false);
  // Resend free tier: 100 emails/day shared with transactional mail, so the
  // campaign goes out in daily batches; already-sent accounts are skipped
  const [launchEmailBatchSize, setLaunchEmailBatchSize] = useState(60);

  const sendLaunchTest = async (withOffer: boolean) => {
    if (!user || !launchCampaignId || !launchTestEmail) return;
    setLaunchEmailRunning(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/marketing/launch-announcement-test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ campaignId: launchCampaignId, toEmail: launchTestEmail, withOffer }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Request failed');
      }
      notifications.show({
        message: `Test (${withOffer ? 'offer' : 'standard'}) sent to ${launchTestEmail}`,
        color: 'blue',
        autoClose: 4000,
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Test send failed',
        color: 'red',
      });
    } finally {
      setLaunchEmailRunning(false);
    }
  };

  const runLaunchEmail = async (dryRun: boolean) => {
    if (!user || !launchCampaignId) return;
    if (!dryRun && !window.confirm(
      `Send the launch email to up to ${launchEmailBatchSize} accounts (of ${launchEmailResult?.counts.eligible ?? '?'} eligible)? This cannot be undone.`
    )) {
      return;
    }
    setLaunchEmailRunning(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/marketing/launch-announcement`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ dryRun, limit: launchEmailBatchSize, campaignId: launchCampaignId }),
      });
      if (!response.ok) throw new Error('Request failed');
      const data = await response.json();
      setLaunchEmailResult(data);
      notifications.show({
        message: dryRun
          ? `Dry run: ${data.counts.eligible} eligible (${data.counts.offerVariant} with offer), next batch ${data.counts.batchSize}`
          : `Sent ${data.counts.sent} emails (${data.counts.failed} failed), ${data.counts.remaining} remaining`,
        color: dryRun ? 'blue' : 'green',
        autoClose: 6000,
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Launch email request failed',
        color: 'red',
      });
    } finally {
      setLaunchEmailRunning(false);
    }
  };

  const handleSeedingConfigPatch = async (
    meal: string,
    patch: { enabled?: boolean; promptCount?: number },
    successMessage: string
  ) => {
    if (!user || !seedingConfig) return;
    setSeedingConfigUpdating(meal);
    const previous = seedingConfig;
    setSeedingConfig(prev => ({
      ...prev,
      [meal]: { ...(prev?.[meal] ?? { enabled: false }), ...patch },
    }));
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/catalog-seeding-config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ config: { [meal]: patch } }),
      });
      if (!response.ok) throw new Error('Request failed');
      notifications.show({
        message: successMessage,
        color: patch.enabled === false ? 'orange' : 'green',
        autoClose: 3000,
      });
    } catch (error) {
      setSeedingConfig(previous);
      notifications.show({
        title: 'Error',
        message: `Failed to update ${meal} seeding`,
        color: 'red',
      });
    } finally {
      setSeedingConfigUpdating(null);
    }
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <Paper withBorder p="md" radius="lg">
          <Stack gap="md">
            <div>
              <Title order={3}>Global Feature Flags</Title>
              <Text size="sm" c="dimmed">
                Toggle features for all users (can be overridden per user)
              </Text>
            </div>

            <Card withBorder radius="lg">
              <Group gap="sm" align="center">
                <Switch
                  checked={!!globalFlags['additional_meals']}
                  disabled={globalFlagUpdating === 'additional_meals'}
                  onChange={(e) =>
                    handleToggleGlobalFlag('additional_meals', e.currentTarget.checked)
                  }
                  label="Breakfast & lunch planning"
                  description="Slots beyond the dinner baseline: rotations, leftover lunches, batch cooking, prep days"
                />
              </Group>
            </Card>
          </Stack>
        </Paper>

        <Paper withBorder p="md" radius="lg">
          <Stack gap="md">
            <div>
              <Title order={3}>Catalog Seeding</Title>
              <Text size="sm" c="dimmed">
                The weekly scheduler seeds whichever meals are enabled here.
                New recipes land in the seed queue for review. Turn a meal off
                once its pool is deep enough.
              </Text>
            </div>

            <Card withBorder radius="lg">
              <Stack gap="sm">
                {(['breakfast', 'lunch', 'dinner'] as const).map((meal) => {
                  const mealConfig = seedingConfig?.[meal];
                  const promptCount = mealConfig?.promptCount ?? seedingDefaults.promptCount;
                  return (
                    <Group key={meal} gap="md" align="center" wrap="wrap">
                      <Switch
                        checked={!!mealConfig?.enabled}
                        disabled={seedingConfig === null || seedingConfigUpdating === meal}
                        onChange={(e) =>
                          handleSeedingConfigPatch(
                            meal,
                            { enabled: e.currentTarget.checked },
                            `Scheduled ${meal} seeding is now ${e.currentTarget.checked ? 'ON' : 'OFF'}`
                          )
                        }
                        label={`Seed ${meal}s`}
                        styles={{ label: { minWidth: 120 } }}
                      />
                      <NumberInput
                        value={promptCount}
                        min={seedingDefaults.promptCountMin}
                        max={seedingDefaults.promptCountMax}
                        step={1}
                        clampBehavior="strict"
                        allowDecimal={false}
                        w={80}
                        size="xs"
                        disabled={seedingConfig === null || !mealConfig?.enabled || seedingConfigUpdating === meal}
                        onChange={(value) => {
                          const next = Number(value);
                          if (!Number.isInteger(next) || next === promptCount) return;
                          handleSeedingConfigPatch(
                            meal,
                            { promptCount: next },
                            `${meal} runs now use ${next} prompt${next === 1 ? '' : 's'}`
                          );
                        }}
                      />
                      <Text size="xs" c="dimmed">
                        prompts per run, about {promptCount * seedingDefaults.recipesPerPrompt} recipes
                      </Text>
                    </Group>
                  );
                })}
              </Stack>
            </Card>
          </Stack>
        </Paper>

        <Paper withBorder p="md" radius="lg">
          <Stack gap="md">
            <div>
              <Group gap={6} align="center">
                <Title order={3}>Launch Emails</Title>
                <Tooltip
                  multiline
                  w={340}
                  withArrow
                  events={{ hover: true, focus: true, touch: true }}
                  label="Eligible accounts: the campaign's flag resolves ON for the user (per-user override or global), an email address exists, and the account has not opted out of lifecycle mail. Expired accounts get the trial-offer variant. Already-sent accounts are skipped, so each press resumes where the last stopped. With the global flag off, per-user flagged accounts are the only recipients, which is the end-to-end test path."
                >
                  <ActionIcon variant="subtle" color="gray" size="sm">
                    <IconInfoCircle size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
              <Text size="sm" c="dimmed">
                One campaign per major release, sent in daily batches until
                remaining hits zero.
              </Text>
            </div>
            <Group gap="sm" align="flex-end">
              <Select
                label="Campaign"
                data={launchCampaigns.map((c) => ({ value: c.id, label: c.label }))}
                value={launchCampaignId}
                onChange={setLaunchCampaignId}
                w={260}
                size="xs"
                placeholder="Select a campaign"
              />
              <TextInput
                label="Test address"
                value={launchTestEmail}
                onChange={(e) => setLaunchTestEmail(e.currentTarget.value)}
                w={220}
                size="xs"
                placeholder="you@example.com"
              />
              <Button
                variant="light"
                size="xs"
                loading={launchEmailRunning}
                disabled={!launchCampaignId || !launchTestEmail}
                onClick={() => sendLaunchTest(false)}
              >
                Test standard
              </Button>
              <Button
                variant="light"
                size="xs"
                loading={launchEmailRunning}
                disabled={!launchCampaignId || !launchTestEmail}
                onClick={() => sendLaunchTest(true)}
              >
                Test offer
              </Button>
            </Group>
            <Group gap="sm" align="flex-end">
              <NumberInput
                label="Batch size"
                description="Capped at 70 of the 100/day free tier, keeping room for the drip, welcome, and verification mail"
                value={launchEmailBatchSize}
                onChange={(value) => {
                  const next = Number(value);
                  if (Number.isInteger(next)) setLaunchEmailBatchSize(Math.min(Math.max(next, 1), 70));
                }}
                min={1}
                max={70}
                w={160}
                size="xs"
              />
              <Button
                variant="outline"
                loading={launchEmailRunning}
                disabled={!launchCampaignId}
                onClick={() => runLaunchEmail(true)}
              >
                Dry run
              </Button>
              <Button
                color="orange"
                loading={launchEmailRunning}
                disabled={!launchCampaignId || !launchEmailResult || !launchEmailResult.dryRun}
                onClick={() => runLaunchEmail(false)}
              >
                Send
              </Button>
            </Group>
            {launchEmailResult && (
              <Card withBorder radius="lg">
                <Stack gap={4}>
                  {!launchEmailResult.flagGloballyEnabled && (
                    <Text size="sm" c="orange.8" fw={600}>
                      Global flag is OFF: only accounts with a per-user
                      override of the campaign flag are eligible (test mode).
                    </Text>
                  )}
                  <Text size="sm">
                    {launchEmailResult.dryRun ? 'Dry run' : 'Send'} result:{' '}
                    {Object.entries(launchEmailResult.counts)
                      .map(([key, value]) => `${key} ${value}`)
                      .join(', ')}
                  </Text>
                </Stack>
              </Card>
            )}
          </Stack>
        </Paper>

        <Paper withBorder p="md" radius="lg">
          <Stack gap="md">
            <Group justify="space-between" align="center">
              <div>
                <Title order={3}>Data Migrations</Title>
                <Text size="sm" c="dimmed">
                  Run database migrations across all users
                </Text>
              </div>
              <Button
                leftSection={<IconRefresh size="1rem" />}
                variant="outline"
                onClick={() => loadMigrations()}
                loading={migrationsLoading}
              >
                Refresh
              </Button>
            </Group>

            {/* flex-basis so the (very wide) type picker takes its own row on
                phones and the buttons wrap below it. */}
            <Group align="flex-end">
              <Select
                label="Migration Type"
                placeholder="Select migration type"
                value={selectedMigrationType}
                onChange={setSelectedMigrationType}
                data={[
                  { value: 'nutrition-backfill', label: 'Nutrition Backfill' },
                  { value: 'dietary-restrictions-backfill', label: 'Dietary Restrictions Backfill (Run First)' },
                  { value: 'dietary-restrictions-logic-backfill', label: 'Dietary Restrictions Logic Backfill (Apply Hierarchies)' },
                  { value: 'dietary-validation-backfill', label: 'Dietary Validation Backfill (Validate & Enrich Restrictions)' },
                  { value: 'recipe-catalog-population', label: 'Recipe Catalog Population + Embeddings (Run Second)' },
                  { value: 'isSaved-backfill', label: 'isSaved Backfill (Set isSaved=false on recipes missing field)' },
                  { value: 'embedding-regeneration', label: 'Embedding Regeneration (Regenerate user recipe embeddings)' },
                  { value: 'catalog-embedding-regeneration', label: 'Catalog Embedding Regeneration (Regenerate ALL catalog recipe embeddings)' },
                  { value: 'taste-axes-backfill', label: 'Taste Axes Backfill (Add formatTags + ingredientKeys to recipes)' },
                  { value: 'ingredient-keys-backfill', label: 'Ingredient Keys Backfill (Recompute ingredientKeys only, user recipes)' },
                  { value: 'catalog-ingredient-keys-backfill', label: 'Catalog Ingredient Keys Backfill (Recompute ingredientKeys on catalog recipes)' },
                  { value: 'catalog-flavor-texture-tags-backfill', label: 'Catalog Flavor & Texture Tags Backfill (Recompute deterministic flavor tags + textureTags; run Catalog Embedding Regeneration AFTER, catalog recipes)' },
                  { value: 'flavor-texture-tags-backfill', label: 'Flavor & Texture Tags Backfill (Recompute deterministic flavor tags + textureTags; run Embedding Regeneration AFTER, user recipes)' },
                  { value: 'normalized-ingredient-names-backfill', label: 'Normalized Ingredient Names Backfill (Index user recipes for ingredient→recipes lookup)' },
                  { value: 'catalog-normalized-ingredient-names-backfill', label: 'Catalog Normalized Ingredient Names Backfill (Index catalog recipes for ingredient→recipes lookup)' },
                  { value: 'shortid-backfill', label: 'Short ID Backfill (Generate shortIds + slug mappings for friendly URLs)' },
                  { value: 'catalog-shortid-backfill', label: 'Catalog Short ID Backfill (Generate shortIds + slug mappings for catalog recipes)' },
                  { value: 'recipe-cost-backfill', label: 'Recipe Cost Backfill (Populate costEstimate on user recipes from ingredient DB)' },
                  { value: 'catalog-cost-backfill', label: 'Catalog Cost Backfill (Populate costEstimate on catalog recipes from ingredient DB)' },
                  { value: 'catalog-dine-out-backfill', label: 'Catalog Dine-Out Backfill (AI-estimate restaurant price for catalog recipes)' },
                  { value: 'recipe-dine-out-backfill', label: 'Recipe Dine-Out Backfill (Set dine-out price on user recipes; reuses catalog estimate when unchanged)' },
                  { value: 'catalog-nutrition-recompute', label: 'Catalog Nutrition Recompute (Repair inflated DB nutrition + upgrade AI/legacy values on full coverage, catalog recipes)' },
                  { value: 'recipe-nutrition-recompute', label: 'Recipe Nutrition Recompute (Repair inflated DB nutrition + upgrade AI/legacy values on full coverage, user recipes)' },
                  { value: 'catalog-cost-recompute', label: 'Catalog Cost Recompute (Force-refresh costEstimate from ingredient DB, catalog recipes)' },
                  { value: 'recipe-cost-recompute', label: 'Recipe Cost Recompute (Force-refresh costEstimate from ingredient DB, user recipes)' },
                  { value: 'catalog-budget-tag-reconcile', label: 'Catalog Budget Tag Reconcile (Apply/strip budget-friendly from stored cost: apply ≤$2.50, strip >$3.00/serving mid, catalog recipes)' },
                  { value: 'recipe-budget-tag-reconcile', label: 'Recipe Budget Tag Reconcile (Apply/strip budget-friendly from stored cost: apply ≤$2.50, strip >$3.00/serving mid, user recipes)' },
                  { value: 'catalog-technique-tag-reconcile', label: 'Catalog Technique Tag Reconcile (Strip technique tags the instructions never evidence: slow cooker/pressure cooker/air fryer/sous vide/sheet pan/grill/bake/roast/no-cook; never adds, catalog recipes)' },
                  { value: 'recipe-technique-tag-reconcile', label: 'Recipe Technique Tag Reconcile (Strip technique tags the instructions never evidence: slow cooker/pressure cooker/air fryer/sous vide/sheet pan/grill/bake/roast/no-cook; never adds, user recipes)' },
                  { value: 'catalog-dietary-restriction-reconcile', label: 'Catalog Dietary Restriction Reconcile (Reconcile High-Protein/Low-Sodium etc. from stored DB/source nutrition; strict strip, 10% apply margin, catalog recipes)' },
                  { value: 'recipe-dietary-restriction-reconcile', label: 'Recipe Dietary Restriction Reconcile (Reconcile High-Protein/Low-Sodium etc. from stored DB/source nutrition; strict strip, 10% apply margin, user recipes)' },
                  { value: 'catalog-low-fodmap-backfill', label: 'Catalog Low-FODMAP Backfill (Ingredient validator strips clear violations; AI elimination-phase determination tags compliant recipes; AI only runs for validator-clean untagged recipes, catalog recipes)' },
                  { value: 'recipe-low-fodmap-backfill', label: 'Recipe Low-FODMAP Backfill (Ingredient validator strips clear violations; AI elimination-phase determination tags compliant recipes; AI only runs for validator-clean untagged recipes, user recipes)' },
                  { value: 'catalog-slots-backfill', label: 'Catalog Slots Backfill (Derive slots {breakfast/lunch/dinner} from category; also sets category=main_dish on pre-category dinner mains; never overwrites existing values, catalog recipes)' },
                  { value: 'recipe-slots-backfill', label: 'Recipe Slots Backfill (Derive slots from category on user recipes; also sets category=main_dish on pre-category ai_generated recipes - plan saves and catalog copies; imports and hand-made recipes stay untagged)' },
                  { value: 'plan-timestamps-backfill', label: 'Plan Timestamps Backfill (Denormalize lastPlanCreatedAt / lastPlanActivatedAt onto user docs for lifecycle-email scans)' },
                  { value: 'activation-signals-backfill', label: 'Activation Signals Backfill (Denormalize mealPlanCount + mealSwapCount + lastSwapAt onto user docs; swaps limited to last 365 days of preference events)' },
                  { value: 'taste-personality-regeneration', label: 'Taste Personality Regeneration (Force-regenerate all taste personality blurbs from scratch; only mentions user-chosen diets)' },
                ]}
                style={{ flex: '1 1 260px' }}
              />
              <Button
                leftSection={<IconRocket size="1rem" />}
                onClick={() => selectedMigrationType && handleStartMigration(selectedMigrationType, false)}
                loading={startingMigration}
                disabled={!selectedMigrationType}
                color="blue"
              >
                Start Migration
              </Button>
              <Button
                leftSection={<IconRocket size="1rem" />}
                variant="outline"
                onClick={() => selectedMigrationType && handleStartMigration(selectedMigrationType, true)}
                loading={startingMigration}
                disabled={!selectedMigrationType}
              >
                Dry Run
              </Button>
            </Group>

            {migrationsLoading && migrations.length === 0 ? (
              <Stack gap="xs">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} height={40} radius="sm" />
                ))}
              </Stack>
            ) : migrations.length === 0 ? (
              <Alert
                icon={<IconDatabase size="1rem" />}
                title="No Migrations"
                color="blue"
                radius="lg"
              >
                No migrations have been run yet.
              </Alert>
            ) : (
              <Table.ScrollContainer minWidth={760}>
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Type</Table.Th>
                      {/* miw so the status badge ("in_progress" + icon) can't
                          be squeezed into "..." by the wider text columns */}
                      <Table.Th miw={140}>Status</Table.Th>
                      <Table.Th>Progress</Table.Th>
                      <Table.Th>Started</Table.Th>
                      <Table.Th>Completed</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {migrations.map((migration) => (
                      <Table.Tr key={migration.id}>
                        <Table.Td>
                          <Text fw={500}>{migration.type}</Text>
                          {migration.dryRun && (
                            <Badge size="xs" variant="light" color="gray">
                              Dry Run
                            </Badge>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            color={
                              migration.status === 'completed' ? 'green' :
                              migration.status === 'failed' ? 'red' :
                              migration.status === 'in_progress' ? 'blue' :
                              'gray'
                            }
                            variant="light"
                            leftSection={
                              migration.status === 'completed' ? <IconCheck size="0.8rem" /> :
                              migration.status === 'failed' ? <IconX size="0.8rem" /> :
                              migration.status === 'in_progress' ? <IconClock size="0.8rem" /> :
                              null
                            }
                          >
                            {migration.status}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {migration.processedUsers} / {migration.totalUsers}
                            {migration.failedUsers > 0 && (
                              <Text size="xs" c="red" component="span" ml="xs">
                                ({migration.failedUsers} failed)
                              </Text>
                            )}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {new Date(migration.startedAt).toLocaleString()}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {migration.completedAt
                              ? new Date(migration.completedAt).toLocaleString()
                              : '-'
                            }
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )}
          </Stack>
        </Paper>

        <Paper withBorder p="md" radius="lg">
          <Stack gap="md">
            <Group justify="space-between" align="center">
              <div>
                <Title order={3}>AI Context Caches</Title>
                <Text size="sm" c="dimmed">
                  Manage cached system instructions for AI operations
                </Text>
              </div>
              <Button
                leftSection={<IconRefresh size="1rem" />}
                variant="outline"
                onClick={loadCaches}
                loading={cachesLoading}
              >
                Refresh
              </Button>
            </Group>

            {cachesLoading && caches.length === 0 ? (
              <Stack gap="xs">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} height={40} radius="sm" />
                ))}
              </Stack>
            ) : caches.length === 0 ? (
              <Alert
                icon={<IconDatabase size="1rem" />}
                title="No Caches Found"
                color="blue"
                radius="lg"
              >
                No context caches are currently active.
              </Alert>
            ) : (
              <Table.ScrollContainer minWidth={760}>
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Display Name</Table.Th>
                      <Table.Th miw={90}>Status</Table.Th>
                      <Table.Th>Tokens</Table.Th>
                      <Table.Th>Created</Table.Th>
                      <Table.Th>Expires</Table.Th>
                      <Table.Th>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {caches.map((cache) => (
                      <Table.Tr key={cache.name}>
                        <Table.Td>
                          <Text fw={500}>{cache.displayName}</Text>
                          <Text size="xs" c="dimmed">
                            {cache.name.split('/').pop() || cache.name}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            color={cache.isExpired ? 'red' : 'green'}
                            variant="light"
                          >
                            {cache.isExpired ? 'Expired' : 'Active'}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {cache.totalTokenCount ? cache.totalTokenCount.toLocaleString() : 'N/A'}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {new Date(cache.createdAt).toLocaleString()}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {new Date(cache.expiresAt).toLocaleString()}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Tooltip label="Delete Cache">
                            <ActionIcon
                              color="red"
                              variant="subtle"
                              onClick={() => handleDeleteCache(cache.name)}
                            >
                              <IconTrash size="1rem" />
                            </ActionIcon>
                          </Tooltip>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )}
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
};
